import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, ArrowRight, Sparkles } from 'lucide-react';

/**
 * FeatureGate — wraps a page/section and shows a translucent upgrade overlay
 * when the current user does not have access to the specified feature.
 *
 * Access is granted when:
 *   - user.plan === 'enterprise_pro', OR
 *   - user.feature_access includes the feature key
 *
 * IMPORTANT: Children are NOT rendered when gated — this prevents useEffect /
 * API calls in child components from firing and causing "failed to load" errors.
 * A static skeleton is shown behind the blur instead.
 *
 * Props:
 *   feature      — string key, e.g. "knowledge_base", "agent_builder", "select_agent"
 *   featureLabel — human-readable name shown in the overlay, e.g. "Knowledge Base"
 *   children     — the content to render (only mounted when user has access)
 */
export default function FeatureGate({ feature, featureLabel, children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const hasAccess =
    user?.plan === 'enterprise_pro' ||
    (user?.feature_access || []).includes(feature);

  if (hasAccess) return <>{children}</>;

  return (
    <div className="relative min-h-[60vh]">
      {/* Static skeleton placeholder — mimics page structure without mounting real components */}
      <div
        className="pointer-events-none select-none"
        style={{ filter: 'blur(6px)', opacity: 0.45 }}
        aria-hidden="true"
      >
        <_GatePlaceholder featureLabel={featureLabel} />
      </div>

      {/* Translucent overlay */}
      <div
        className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm"
      >
        <div
          className="flex flex-col items-center text-center px-8 py-10 rounded-2xl max-w-md mx-4 bg-white dark:bg-slate-800 border border-black/5 dark:border-white/10 shadow-xl dark:shadow-2xl"
        >
          {/* Lock icon */}
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
            style={{ background: 'rgba(248,114,23,0.08)' }}
          >
            <Lock className="w-8 h-8 text-[#F87217]" />
          </div>

          {/* Badge */}
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(248,114,23,0.1)', color: '#F87217' }}
          >
            <Sparkles className="w-3 h-3" />
            ENTERPRISE PRO
          </span>

          {/* Heading */}
          <h2
            className="text-xl font-extrabold text-slate-900 dark:text-slate-100 mb-2"
            style={{ fontFamily: "'Outfit',sans-serif", letterSpacing: '-0.02em' }}
          >
            Upgrade to Unlock
          </h2>

          {/* Description */}
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
            <strong className="text-slate-700 dark:text-slate-300">{featureLabel}</strong> is available
            exclusively on the <strong className="text-[#F87217]">Enterprise Pro</strong> plan.
            Upgrade now to access this premium feature.
          </p>

          {/* CTA */}
          <button
            onClick={() => navigate('/pricing')}
            className="group flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
              boxShadow: '0 4px 16px rgba(248,114,23,0.35)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.5)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(248,114,23,0.35)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Upgrade Now
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>

          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-4">
            Current plan: <span className="font-semibold capitalize">{user?.plan?.replace('_', ' ') || 'Free'}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline helper — use inside a card/section to show a compact lock prompt
 * instead of the full-page overlay. E.g. the Dashboard "Select Agent" section.
 */
export function InlineFeatureGate({ feature, featureLabel, children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const hasAccess =
    user?.plan === 'enterprise_pro' ||
    (user?.feature_access || []).includes(feature);

  if (hasAccess) return <>{children}</>;

  return (
    <div className="relative">
      <div
        className="pointer-events-none select-none"
        style={{ filter: 'blur(4px)', opacity: 0.35 }}
        aria-hidden="true"
      >
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10">
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(248,114,23,0.08)' }}
        >
          <Lock className="w-3.5 h-3.5 text-[#F87217]" />
          <span className="text-xs font-bold text-[#F87217]">Enterprise Pro</span>
        </div>
        <button
          onClick={() => navigate('/pricing')}
          className="text-xs font-semibold text-[#F87217] hover:text-[#C65B12] underline underline-offset-2 transition-colors"
        >
          View plans to unlock {featureLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Static skeleton shown behind the blur when the real content is gated.
 * This avoids mounting the actual child component (and its useEffect / API calls).
 */
function _GatePlaceholder({ featureLabel }) {
  return (
    <div className="p-8 space-y-6">
      {/* Fake header */}
      <div className="flex items-center justify-between">
        <div className="h-8 w-56 rounded-lg bg-slate-200 dark:bg-slate-700" />
        <div className="h-9 w-32 rounded-lg bg-slate-200 dark:bg-slate-700" />
      </div>
      {/* Fake toolbar */}
      <div className="flex gap-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-8 w-24 rounded-full bg-slate-100 dark:bg-slate-800" />
        ))}
      </div>
      {/* Fake content cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700 p-5 space-y-3">
            <div className="h-5 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-4 w-full rounded bg-slate-100 dark:bg-slate-800" />
            <div className="h-4 w-2/3 rounded bg-slate-100 dark:bg-slate-800" />
            <div className="flex gap-2 pt-2">
              <div className="h-6 w-16 rounded-full bg-slate-100 dark:bg-slate-800" />
              <div className="h-6 w-20 rounded-full bg-slate-100 dark:bg-slate-800" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}